import { createContext, useCallback, useContext, useMemo } from 'react';

import { useProfile } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import { Users } from '@mint-sdk/api-ai-assistant';

import {
  getKnowledgeBaseAccess,
  getRowActions,
  type KnowledgeBaseAccess,
  type RowAction,
} from '../../helpers/knowledge-base/knowledgeBaseAccess.js';
import { isUnprocessedStatus } from '../../helpers/ragDocumentStatus.js';
import type { RagTableType } from '../../types/rag.js';
import {
  useAiHubPermissionLevel,
  useAiHubPermissions,
} from '../useAiHubPermissions.jsx';
import { useDocumentActions } from './useDocumentActions.js';

export const useKnowledgeBaseAccess = (): KnowledgeBaseAccess => {
  const { profileService } = useProfile();
  const aiHubLevel = useAiHubPermissionLevel();
  const profile = profileService.getProfileData();

  return useMemo(
    () =>
      getKnowledgeBaseAccess({
        isOwner: profile?.isOwner,
        email: profile?.email,
        aiHubLevel,
      }),
    [profile?.isOwner, profile?.email, aiHubLevel],
  );
};

// aiFileSystem cleanup: delete this and the workspace feature gates.
const getLegacyRowActions = (
  canDownload: boolean,
  canDelete: boolean,
): RowAction[] => {
  const actions: RowAction[] = [];

  if (canDownload) actions.push('download');
  if (canDelete) actions.push('delete');

  return actions;
};

type Row = Pick<RagTableType, 'fileId' | 'fileName' | 'userId' | 'status'>;

type RowActions = {
  isFileSystemEnabled: boolean;
  getActions: (row: Row) => RowAction[];
  download: (row: Row) => Promise<void>;
};

export const useRowActions = (): RowActions => {
  const {
    aiFileSystem: { enabled: isFileSystemEnabled },
  } = useFlags('@mint/arm', ['aiFileSystem']);
  const { canDownloadKBFile, canDeleteKBFile } = useAiHubPermissions();
  const access = useKnowledgeBaseAccess();
  const { data: me } = Users.getMe.use({});
  const { onDocumentDownload } = useDocumentActions();
  const myUserId = me?.userId;

  const getActions = useCallback(
    (row: Row): RowAction[] => {
      const actions = isFileSystemEnabled
        ? getRowActions(access, myUserId != null && row.userId === myUserId)
        : getLegacyRowActions(canDownloadKBFile, canDeleteKBFile);

      // An unprocessed file can only be deleted.
      return isUnprocessedStatus(row.status)
        ? actions.filter((action) => action === 'delete')
        : actions;
    },
    [isFileSystemEnabled, access, myUserId, canDownloadKBFile, canDeleteKBFile],
  );

  return { isFileSystemEnabled, getActions, download: onDocumentDownload };
};

export const RowActionsContext = createContext<RowActions | null>(null);

export const useRowActionsContext = () => {
  const value = useContext(RowActionsContext);

  if (!value) throw new Error('Knowledge base rows need a RowActionsProvider');

  return value;
};
